import { startOfDay, endOfDay } from 'date-fns';
import type { FinanceTransaction } from '@/types/finance';

/**
 * Extrato da conta: movimentos realizados (status completed) de UMA conta,
 * com saldo corrente linha a linha.
 *
 * A matemática espelha o trigger update_account_balance() do Postgres:
 * - income: +(amount + interest + fine) na conta
 * - expense: -(amount + interest + fine)
 * - transfer: -amount na origem, +amount no destino (SEM juros/multa)
 * - só status 'completed' afeta saldo
 *
 * refDate = paymentDate ?? dueDate (mesma convenção da DRE). Nota: os
 * relatórios "pagas/recebidas" EXCLUEM completed sem paymentDate; aqui o
 * fallback é intencional para o saldo bater com o current_balance do banco.
 */

export type StatementDirection = 'in' | 'out';

export interface StatementMovement {
  direction: StatementDirection;
  amount: number;
  counterpartAccountId?: string;
}

export interface StatementEntry {
  tx: FinanceTransaction;
  refDate: Date;
  direction: StatementDirection;
  /** Valor absoluto do movimento (encargos inclusos quando aplicável). */
  amount: number;
  /** +amount (in) ou -amount (out). */
  signedAmount: number;
  /** Saldo APÓS esta linha. */
  runningBalance: number;
  isTransfer: boolean;
  /** A outra ponta da transferência (origem ou destino), quando transfer. */
  transferCounterpartAccountId?: string;
}

export interface AccountStatement {
  openingBalance: number;
  rows: StatementEntry[];
  closingBalance: number;
  totalIn: number;
  /** Positivo. */
  totalOut: number;
  count: number;
}

export interface AccountStatementInput {
  transactions: FinanceTransaction[];
  accountId: string;
  /** initialBalance da conta (o saldo anterior parte dele). */
  initialBalance: number;
  fromDate: Date;
  toDate: Date;
}

/**
 * Movimento de uma transação na conta, ou null se não afeta a conta.
 * Espelha o trigger do banco (ver doc do módulo).
 */
export function movementFor(
  tx: FinanceTransaction,
  accountId: string,
): StatementMovement | null {
  if (tx.status !== 'completed') return null;

  if (tx.type === 'transfer') {
    const isSource = tx.accountId === accountId;
    const isDestination = tx.destinationAccountId === accountId;
    // Self-transfer: net zero no trigger — não gera linha.
    if (isSource && isDestination) return null;
    if (isSource) {
      return {
        direction: 'out',
        amount: tx.amount,
        ...(tx.destinationAccountId && { counterpartAccountId: tx.destinationAccountId }),
      };
    }
    if (isDestination) {
      return { direction: 'in', amount: tx.amount, counterpartAccountId: tx.accountId };
    }
    return null;
  }

  if (tx.accountId !== accountId) return null;
  const effective = tx.amount + (tx.interest ?? 0) + (tx.fine ?? 0);
  return { direction: tx.type === 'income' ? 'in' : 'out', amount: effective };
}

const refDateOf = (tx: FinanceTransaction): Date => tx.paymentDate ?? tx.dueDate;

export function buildAccountStatement(input: AccountStatementInput): AccountStatement {
  const { transactions, accountId, initialBalance, fromDate, toDate } = input;
  const start = startOfDay(fromDate);
  const end = endOfDay(toDate);

  let openingDelta = 0;
  const collected: { tx: FinanceTransaction; refDate: Date; movement: StatementMovement }[] = [];

  for (const tx of transactions) {
    const movement = movementFor(tx, accountId);
    if (!movement) continue;
    const refDate = refDateOf(tx);
    const signed = movement.direction === 'in' ? movement.amount : -movement.amount;
    if (refDate.getTime() < start.getTime()) {
      openingDelta += signed;
    } else if (refDate.getTime() <= end.getTime()) {
      collected.push({ tx, refDate, movement });
    }
    // refDate > end: fora do extrato e do saldo anterior.
  }

  collected.sort((a, b) => {
    const byDate = a.refDate.getTime() - b.refDate.getTime();
    if (byDate !== 0) return byDate;
    const byCreated = a.tx.createdAt.getTime() - b.tx.createdAt.getTime();
    if (byCreated !== 0) return byCreated;
    return a.tx.id.localeCompare(b.tx.id);
  });

  const openingBalance = initialBalance + openingDelta;
  let running = openingBalance;
  let totalIn = 0;
  let totalOut = 0;

  const rows: StatementEntry[] = collected.map(({ tx, refDate, movement }) => {
    const signedAmount = movement.direction === 'in' ? movement.amount : -movement.amount;
    running += signedAmount;
    if (movement.direction === 'in') totalIn += movement.amount;
    else totalOut += movement.amount;
    return {
      tx,
      refDate,
      direction: movement.direction,
      amount: movement.amount,
      signedAmount,
      runningBalance: running,
      isTransfer: tx.type === 'transfer',
      ...(movement.counterpartAccountId && {
        transferCounterpartAccountId: movement.counterpartAccountId,
      }),
    };
  });

  return {
    openingBalance,
    rows,
    closingBalance: running,
    totalIn,
    totalOut,
    count: rows.length,
  };
}
